<!-- eslint-disable max-len -->
<template>
  <div v-if="result">
    <!-- Hero -->
    <div class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-teal-900 to-teal-700 p-6 text-white shadow-lg">
      <div
        aria-hidden="true"
        class="pointer-events-none absolute -right-16 -top-20 h-60 w-60 rounded-full bg-teal-300/30 blur-3xl"
      />

      <span class="relative inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-teal-50">
        <SparklesIcon class="size-3.5" />
        {{ t('livvia.result.badge') }}
      </span>

      <h2 class="relative mt-3 text-2xl font-bold tracking-tight">
        {{ t('livvia.result.headline') }}
      </h2>

      <div class="relative mt-2 flex flex-wrap items-baseline gap-3">
        <span class="text-4xl font-bold tracking-tight">
          {{ money(result.totalAmount) }}
        </span>
        <span class="text-sm font-semibold text-teal-100">
          {{ t('livvia.result.summary', { clients: result.clientCount }) }}
        </span>
      </div>

      <span class="relative mt-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-teal-50">
        <ClockIcon class="size-3.5" />
        {{ t('livvia.result.timeSaved', { hours: result.timeSavedHours }) }}
      </span>
    </div>

    <!-- Cart list -->
    <div class="mt-4 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
      <div class="flex flex-wrap items-center gap-3 border-b border-gray-100 p-4">
        <h3 class="text-base font-semibold text-gray-900">
          {{ t('livvia.result.cartsTitle') }}
        </h3>
        <button
          type="button"
          class="ml-auto inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
          :disabled="livvia.allChargesSent"
          @click="livvia.chargeAll()"
        >
          <PaperAirplaneIcon class="size-4" />
          {{ t('livvia.result.actionChargeAll', { count: result.carts.length }) }}
        </button>
      </div>

      <ul class="divide-y divide-gray-100">
        <li
          v-for="cart in result.carts"
          :key="cart.id"
          class="flex flex-wrap items-center gap-3 px-4 py-3"
        >
          <span
            class="flex size-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white"
            :class="cart.avatarClass"
          >
            {{ cart.initials }}
          </span>
          <div class="min-w-0 flex-1">
            <p class="truncate text-sm font-semibold text-gray-900">
              {{ cart.handle }}
            </p>
            <p class="mt-0.5 truncate text-xs text-gray-500">
              {{ cart.items }}
            </p>
          </div>
          <span class="text-base font-bold text-gray-900">
            {{ money(cart.amount) }}
          </span>

          <span
            v-if="isSent(cart.id)"
            class="inline-flex items-center gap-1 rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700"
          >
            <PaperAirplaneIcon class="size-3" />
            {{ t('livvia.result.sentBadge') }}
          </span>
          <button
            v-else
            type="button"
            class="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isCharging(cart.id)"
            @click="livvia.chargeCart(cart.id)"
          >
            <span
              v-if="isCharging(cart.id)"
              class="size-3 animate-spin rounded-full border-2 border-white/40 border-t-white"
              aria-hidden="true"
            />
            {{ isCharging(cart.id) ? t('livvia.result.actionCharging') : t('livvia.result.actionCharge') }}
          </button>
        </li>
      </ul>

      <div class="border-t border-gray-100 bg-gray-50 px-4 py-3 text-xs text-gray-500">
        {{ t('livvia.result.footer', { shown: result.carts.length, total: result.clientCount }) }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  SparklesIcon, ClockIcon, PaperAirplaneIcon,
} from '@heroicons/vue/24/solid';
import useLivviaStore from '@/store/livvia';

const { t } = useI18n();
const livvia = useLivviaStore();

const result = computed(() => livvia.resultData);
const isSent = (id: string) => livvia.sentCarts.has(id);
const isCharging = (id: string) => livvia.chargingCarts.has(id);

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
const money = (n: number) => brl.format(n);
</script>
